import React, { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { 
    Store, Phone, ShoppingBag, Package, Plus, 
    ArrowLeft, ExternalLink, MessageCircle,
    CheckCircle2, Star, ShieldCheck, MapPin, Share2, 
    Facebook, Copy, Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PaymentModal } from "@/components/marketplace/PaymentModal";
import { AddToCartModal } from "@/components/marketplace/AddToCartModal";
import { CartModal } from "@/components/marketplace/CartModal";
import { ListRequestModal } from "@/components/marketplace/ListRequestModal";
import { ListRequestsStatusModal } from "@/components/marketplace/ListRequestsStatusModal";
import { ClipboardList, History } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "https://bvglinkamazon.com") + "/api";

export const StorePage = () => {
    const { sellerId } = useParams();
    const navigate = useNavigate();
    const [seller, setSeller] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showListModal, setShowListModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);

    const addToCart = (productWithQty) => {
        setCart(prev => {
            const existing = prev.find(i => i.id === productWithQty.id);
            if (existing) {
                return prev.map(i => i.id === productWithQty.id ? { ...i, qty: i.qty + productWithQty.qty } : i);
            }
            return [...prev, productWithQty];
        });
        toast.success(`${productWithQty.qty} ajoute nan panye w!`);
    };

    const removeFromCart = (id) => setCart(prev => prev.filter(i => i.id !== id));
    const clearCart = () => setCart([]);
    const updateQuantity = (id, newQty) => {
        if (newQty < 1) return;
        setCart(prev => prev.map(i => i.id === id ? { ...i, qty: newQty } : i));
    };

    useEffect(() => {
        if (sellerId) {
            fetchStoreData();
        }

        // Handle success redirect from MonCash
        const params = new URLSearchParams(window.location.search);
        if (params.get("transactionId")) {
            const txId = params.get("transactionId");
            verifyMoncashTransaction(txId);
        } else if (params.get("payment") === "success") {
            toast.success("Peman ou an reyisi! ✅ Machann nan pral delivre atik ou yo.");
            setShowStatusModal(true);
        }
    }, [sellerId]);

    const verifyMoncashTransaction = async (txId) => {
        try {
            const res = await axios.post(`${API}/marketplace/orders/verify-transaction`, { transaction_id: txId });
            if (res.data.success) {
                toast.success("Peman MonCash ou an verifye avèk siksè! ⚡ Machann an ap prepare kòmand lan.");
            } else {
                toast.error("Nou poko ka verifye peman sa. Re-tcheke pita.");
            }
            setShowStatusModal(true);
        } catch (e) {
            console.error(e);
            toast.error("Pwoblèm pou verifye peman an oswa kòmand lan te deja verifye.");
        }
    };

    const fetchStoreData = async () => {
        setLoading(true);
        try {
            const [sellerRes, prodRes] = await Promise.all([
                axios.get(`${API}/sellers/${sellerId}`),
                axios.get(`${API}/marketplace/products?seller_id=${sellerId}`)
            ]);
            setSeller(sellerRes.data);
            // Filtre pwodwi yo pou nou pa montre sa ki pou POS sèlman (sèlman sa ki pou MARKET oswa BOTH)
            const publicProducts = prodRes.data.filter(p => p.display_on !== 'pos');
            setProducts(publicProducts);
        } catch (error) {
            console.error("Error fetching store data:", error);
            toast.error("Pa kapab chaje magazen sa a.");
            navigate("/marketplace");
        } finally {
            setLoading(false);
        }
    };

    const handleShareStore = (platform) => {
        const storeUrl = window.location.href;
        const storeName = seller?.store_name || seller?.fullname || "BVGLink Store";
        const shareText = `Tcheke magazen mwen an sou BVGLink! Mwen gen bèl pwodwi pou ou: ${storeName}`;
        
        let url = "";
        switch (platform) {
            case "whatsapp":
                url = `https://wa.me/?text=${encodeURIComponent(shareText + " " + storeUrl)}`;
                break;
            case "facebook":
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storeUrl)}`;
                break;
            default:
                navigator.clipboard.writeText(storeUrl);
                toast.success("Link magazen an kopye! ✅");
                return;
        }
        if (url) window.open(url, "_blank");
    };

    const handleShareProduct = (e, product) => {
        e.stopPropagation();
        const productUrl = `${window.location.origin}/marketplace?product=${product.id}`;
        const shareText = `Tcheke bèl pwodwi sa a nan BVGLink: ${product.title} - ${product.price_htg} HTG`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + " " + productUrl)}`;
        window.open(whatsappUrl, "_blank");
    };

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-50">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="loading-spinner"></div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Header />

            {/* STORE HERO */}
            <div className="bg-[#232F3E] text-white py-16 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF9900]/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center sm:text-left">
                    <div className="flex flex-col sm:flex-row items-center gap-8">
                        <div className="w-24 h-24 sm:w-32 sm:h-32 bg-[#FF9900] rounded-[30px] flex items-center justify-center shadow-2xl shadow-black/20 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                             <Store className="w-12 h-12 sm:w-16 sm:h-16 text-black" />
                        </div>
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mb-2">
                                <h1 className="text-4xl sm:text-6xl font-black italic tracking-tighter uppercase leading-none">
                                    {seller?.store_name || seller?.fullname}
                                </h1>
                                <div className="flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/10 backdrop-blur-md">
                                    <ShieldCheck className="w-3 h-3 text-[#FF9900]" />
                                    <span>Machann Verifye</span>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 text-gray-300 font-bold uppercase tracking-widest text-[11px]">
                                <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-[#FF9900]" />
                                    <span>{seller?.phone || "Nimewo telefòn pa disponib"}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ShoppingBag className="w-4 h-4 text-[#FF9900]" />
                                    <span>{products.length} Pwodwi Afiche</span>
                                </div>
                                <div className="flex items-center gap-2">
                                     <MapPin className="w-4 h-4 text-[#FF9900]" />
                                     <span>Ayiti nèt</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-6 sm:mt-0 justify-center sm:justify-start">
                            <Button 
                                onClick={() => {
                                    const token = localStorage.getItem("buyer_token");
                                    if (!token) {
                                        toast.error("Ou dwe konekte oswa kreye yon kont anvan ou kreye yon lis");
                                        navigate("/linkamazon");
                                        return;
                                    }
                                    setShowListModal(true);
                                }}
                                className="bg-[#FF9900] hover:bg-black text-black hover:text-white rounded-2xl h-14 px-8 font-black uppercase tracking-widest italic flex gap-3 shadow-xl shadow-orange-900/10 border-2 border-transparent hover:border-orange-500 transition-all duration-500"
                            >
                                <ClipboardList className="w-5 h-5" /> Kreye yon Lis
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* PRODUCTS GRID */}
            <main className="flex-1 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-200">
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => navigate(-1)}
                                className="rounded-full bg-white shadow-sm hover:shadow-md transition-all"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <h2 className="text-2xl font-black tracking-tight text-gray-900">Tout <span className="text-[#FF9900]">Atik</span> yo</h2>
                        </div>
                    </div>

                    {products.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {products.map((product) => (
                                <Card 
                                    key={product.id}
                                    className="border-none shadow-premium hover:shadow-premium-hover transition-all duration-500 rounded-[35px] overflow-hidden group bg-white flex flex-col h-full cursor-pointer"
                                    onClick={() => {
                                        const token = localStorage.getItem("buyer_token");
                                        if (!token) {
                                            toast.error("Ou dwe konekte oswa kreye yon kont pou achte pwodwi sa a");
                                            navigate("/linkamazon");
                                            return;
                                        }
                                        setSelectedProduct(product);
                                        setShowPayModal(true);
                                    }}
                                >
                                    <div className="aspect-[4/3] relative overflow-hidden bg-gray-50">
                                        {product.images?.[0] ? (
                                            <img 
                                                src={product.images[0]} 
                                                alt={product.title} 
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Package className="w-12 h-12 text-gray-200" />
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4 flex flex-col gap-2">
                                            {product.condition === 'new' && (
                                                <span className="bg-[#FF9900] text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg shadow-xl shadow-orange-500/20">NEW</span>
                                            )}
                                            <button 
                                                onClick={(e) => handleShareProduct(e, product)}
                                                className="bg-white/90 backdrop-blur-md p-2 rounded-xl text-green-600 shadow-lg hover:scale-110 transition-transform border border-green-100"
                                                title="Pataje pwodwi sa a"
                                            >
                                                <Share2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <CardContent className="p-6 flex flex-col flex-1">
                                        <h3 className="text-lg font-black text-gray-900 mb-2 line-clamp-2 leading-tight group-hover:text-[#FF9900] transition-colors">{product.title}</h3>
                                        {product.description && (
                                            <div className="mb-3 border-b border-gray-50 pb-3">
                                                <p className="text-[11px] text-gray-500 font-medium max-h-[60px] overflow-y-auto custom-scrollbar leading-relaxed pr-2">{product.description}</p>
                                            </div>
                                        )}
                                        <div className="mt-auto flex items-end justify-between">
                                            <div>
                                                <div className="flex items-center gap-1 mb-1">
                                                    <div className="flex text-amber-500 scale-75 -ml-2">
                                                        <Star className="w-4 h-4 fill-current" />
                                                        <Star className="w-4 h-4 fill-current" />
                                                        <Star className="w-4 h-4 fill-current" />
                                                        <Star className="w-4 h-4 fill-current" />
                                                        <Star className="w-4 h-4 fill-current" />
                                                    </div>
                                                </div>
                                                <p className="text-3xl font-black text-gray-900 italic tracking-tighter">
                                                    {product.price_htg.toLocaleString()} <span className="text-[10px] text-gray-400 not-italic uppercase font-bold tracking-widest">HTG</span>
                                                </p>
                                            </div>
                                            {(() => {
                                                const cartItem = cart.find(i => i.id === product.id);
                                                if (cartItem) {
                                                    return (
                                                        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl" onClick={e => e.stopPropagation()}>
                                                            <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-lg" onClick={() => updateQuantity(product.id, cartItem.qty - 1)} disabled={cartItem.qty <= 1}>-</Button>
                                                            <span className="w-6 text-center font-black">{cartItem.qty}</span>
                                                            <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-lg" onClick={() => { if (cartItem.qty >= (product.stock ?? 9999)) { toast.error('Pa gen ase estòk!'); return; } updateQuantity(product.id, cartItem.qty + 1); }}>+</Button>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-[#FF9900] group-hover:text-black transition-all duration-500 group-hover:rotate-12" onClick={(e) => { e.stopPropagation(); addToCart({ ...product, qty: 1 }); }}>
                                                        <Plus className="w-6 h-6" />
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <div className="py-24 text-center">
                            <ShoppingBag className="w-16 h-16 text-gray-200 mx-auto mb-6" />
                            <p className="text-gray-500 font-bold uppercase tracking-widest">Poko gen okenn pwodwi nan magazen sa a.</p>
                        </div>
                    )}
                </div>
            </main>

            <Footer />

            {/* FLOATING CART BUTTON */}
            <button 
                onClick={() => setIsCartOpen(true)}
                className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-[#FF9900] to-[#E88B00] text-black w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:scale-105 transition-all"
            >
                <div className="relative">
                    <ShoppingBag className="w-7 h-7" />
                    {cart.length > 0 && (
                        <span className="absolute -top-3 -right-3 bg-red-500 text-white w-6 h-6 flex items-center justify-center rounded-full text-xs font-black border-2 border-white">
                            {cart.reduce((a,b)=>a+b.qty,0)}
                        </span>
                    )}
                </div>
            </button>

            {/* PRODUCT DETAIL / PAYMENT MODAL */}
            {showPayModal && selectedProduct && (
                <AddToCartModal
                    onClose={() => setShowPayModal(false)}
                    product={selectedProduct}
                    onAddToCart={addToCart}
                />
            )}

            {isCartOpen && (
                <CartModal
                    cartItems={cart}
                    onClose={() => setIsCartOpen(false)}
                    onRemoveFromCart={removeFromCart}
                    onClearCart={clearCart}
                    onUpdateQuantity={updateQuantity}
                    sellerId={sellerId}
                />
            )}

            {showListModal && seller && (
                <ListRequestModal
                    seller={seller}
                    onClose={() => setShowListModal(false)}
                />
            )}

            {showStatusModal && (
                <ListRequestsStatusModal
                    onClose={() => setShowStatusModal(false)}
                />
            )}
        </div>
    );
};


